/**
 * `scriptCommandCatalog.ts` — the vocabulary of commands a workflow script may
 * contain, and their argument schemas.
 *
 * A workflow script step is `{ tool: <id>, args: { … } }`, and those args must
 * be exactly what a live tool call would take. The Workflow Builder role,
 * however, is deliberately NOT granted the scene tools: it AUTHORS them into
 * `workflow.yaml` and must never execute one against the author's live scene.
 * So it never sees their schemas the way a normal role does — it needs the
 * SPECIFICATION instead. This module is that specification's single source, and
 * it is filled from the same registrations the executor dispatches on, so the
 * published documentation cannot drift from the real tools.
 *
 * Filled at registry construction (`setScriptCommandCatalog`). Read by:
 *   - `WorkflowYaml.ts` — validating a script step's `args` (keys, types, enums,
 *     required-ness) at parse time, so a bad argument is refused where the
 *     author can see it rather than skipped mid-lesson;
 *   - `WorkflowSchemaCatalog.ts` — rendering the command reference that
 *     `get_workflow_schema` serves.
 *
 * It lives in `ai/registry/` rather than beside the workflow model because the
 * data is the registry's; the workflow layer may import it, but the reverse
 * would cycle (`Workflow_Registry` → schema catalog → `WorkflowYaml`). That is
 * the same indirection the narrower `toolArgKeys` shim had before this module
 * replaced it.
 *
 * ## The three-way split (port of Unity's `IScriptClassifierToolInterface`)
 *
 * Unity's `ScriptClassifier` — the sub-agent that turned an editor prompt into
 * recorded Action steps — divided every registered tool three ways, and the
 * same division is exactly what a workflow-script vocabulary needs:
 *
 *   - **excluded** (`s_filteredToolNames`) — never in a script. Project
 *     lifecycle, role/personality, conversation control, panel toggles. A
 *     script that opens a project or switches role is not a lesson step.
 *   - **context** (`s_executeOnlyToolNames`) — read-only lookups the AUTHORING
 *     agent runs FOR REAL to resolve references, and never records. Calling
 *     `Get_Scene_Context` during playback would return text to nobody.
 *   - **authorable** — the complement: everything else the registry offers.
 *
 * Taking the complement (rather than an allow-list) is deliberate and is
 * Unity's own choice: a newly registered scene tool becomes authorable, and
 * documented, with no edit here. Only a tool that must NOT be authored needs a
 * line added.
 */

import { AgentToolNames } from "./agentToolNames";

/** The subset of `AIAgentTool` this catalogue retains. */
export interface ScriptCommandSpec {
  /** Canonical wire id, exactly as a script step must spell it. */
  readonly name: string;
  /** The registry's model-facing description. */
  readonly description: string;
  /** Grouping used to order the published reference. */
  readonly category: string;
  /** JSON-Schema `properties` for the tool's arguments. */
  readonly properties: ReadonlyMap<string, ScriptCommandArg>;
  /** Argument names the tool cannot run without. */
  readonly required: ReadonlyArray<string>;
  /**
   * True for a command the runner implements itself rather than dispatching to
   * a registered tool (`play_video`, `wait_for`, `do_not_wait`). Such a command
   * is valid in a script and is NOT a live agent tool — see
   * {@link SCRIPT_ONLY_COMMANDS}.
   */
  readonly isScriptOnly: boolean;
}

/** One argument's schema, flattened to what validation and documentation need. */
export interface ScriptCommandArg {
  readonly name: string;
  /** `string` | `number` | `boolean` | `array` | `object`, or "" when unstated. */
  readonly type: string;
  /** Element type for an array argument, or "" when unstated. */
  readonly itemType: string;
  readonly description: string;
  /** Allowed values for an enum argument; empty when unconstrained. */
  readonly enumValues: ReadonlyArray<string>;
  readonly required: boolean;
}

// ---------------------------------------------------------------------------
// The three-way split
// ---------------------------------------------------------------------------

/**
 * Never authorable into a script. Port of Unity's `s_filteredToolNames`, plus
 * the web-only entries noted below.
 *
 * The test to apply when adding one: would a LEARNER, mid-lesson, be served by
 * this happening? Opening a project, switching the agent's role, resetting the
 * conversation and toggling a debug panel all fail it.
 */
export const K_SCRIPT_EXCLUDED_TOOL_IDS: ReadonlySet<string> = new Set(
  [
    // --- Unity's `s_filteredToolNames`, verbatim ---
    AgentToolNames.GetAvailableProjects,
    AgentToolNames.OpenProject,
    AgentToolNames.CreateNewProject,
    AgentToolNames.ExitProject,
    AgentToolNames.SwitchRole,
    AgentToolNames.ChangePersonality,
    AgentToolNames.ResetConversation,
    AgentToolNames.Undo,
    AgentToolNames.ToggleGuide,
    AgentToolNames.ListenUser,
    AgentToolNames.SendUserText,
    AgentToolNames.SaveRecordingAsAction,
    AgentToolNames.ToggleAIPanel,
    AgentToolNames.ToggleDebugMode,
    AgentToolNames.ToggleSpark,
    AgentToolNames.StandHere,
    AgentToolNames.StandWhereverYouWant,
    AgentToolNames.PresentOptions,

    // --- web-only additions ---
    // A script may not move the run it belongs to: the step it lands on plays
    // its own on-start script, so two steps pointing at each other ping-pong
    // with no learner input. `WorkflowScriptRunner.validateSteps` refuses it;
    // excluding it here means the model is never shown it as an option.
    AgentToolNames.GoToWorkflowStep,
    // Worse than moving the run: this ENDS it. Everything after such a step
    // would play against a torn-down run, and for a viewer the project would be
    // gone from under the rest of the script.
    AgentToolNames.StopWorkflow,
    // …and a script may not START one either. It only ever plays inside a run
    // or an authoring test, so the step would restart the playback it belongs
    // to — or, during a test, begin a preview nobody asked for.
    AgentToolNames.StartWorkflow,
    // Acts on the RUN's media panel, not on the scene. Scripts get the narrower
    // `play_video` command below; exposing the Guide's whole transport here
    // would let authored playback pause, seek or collapse a learner's panel.
    AgentToolNames.ControlMedia,
    // A PANEL toggle, not a scene command. Both of these move furniture on the
    // screen of whoever is watching: authored into a step they would open or
    // close a panel for every learner at a moment the author cannot see, and
    // the outline one would fight the learner's own button. Neither leaves any
    // trace in the scene, so a `revert` could not put them back either.
    AgentToolNames.ToggleWorkflowOutline,
    AgentToolNames.ToggleTranscript,
    // The workflow tools edit or read the document a script lives inside.
    // Authoring one into a script is a lesson step that rewrites the lesson.
    AgentToolNames.GetWorkflow,
    AgentToolNames.GetWorkflowSchema,
    AgentToolNames.UpdateWorkflow,
    AgentToolNames.PlayWorkflowScript,
    // Loading a skill changes the AGENT's prompt, not the learner's scene.
    AgentToolNames.LoadSkill,
    AgentToolNames.UseConstantListening,
    AgentToolNames.ChangeWakeGesture,

    // Spark Preview stubs (`executor/previewNotImplemented.ts`). They are
    // registered on purpose — a tool that refuses out loud beats an agent
    // answering a "show me where it says that" from imagination — but they
    // ALWAYS return `tool_error: not_implemented`. In a live turn that is
    // honest; authored into a script it is a lesson step guaranteed to fail
    // for every learner, forever. Remove each from this list when its real
    // executor lands.
    AgentToolNames.OpenDocAtReference,
    AgentToolNames.GoToNextDocResult,
    AgentToolNames.SearchInOpenDoc,
  ].map((id) => id.toLowerCase()),
);

/**
 * The commands the published reference DOCUMENTS — the supported vocabulary for
 * a guided-training script.
 *
 * An explicit allow-list, not the complement of the two sets above. Unity's
 * `ScriptClassifier` takes the complement because it is recording arbitrary
 * user intent ("do what I just said") into an Action; a workflow step is a much
 * narrower thing, and offering the model the whole registry put `delete_model`
 * and `delete_scene` in front of it as lesson steps. A lesson shows, hides,
 * points at and waits — it does not restructure the author's project.
 *
 * Ordered as a lesson reads: show/hide, then emphasis, then motion, then the
 * waits. To support another command, add it here — its description and
 * arguments are then generated from the registry with no further edit.
 *
 * NOTE this list governs DOCUMENTATION only. A registered tool outside it is
 * still parsed and still plays ({@link isAuthorableScriptCommand}), because
 * documents already exist that use `go_to_scene` or `focus_on_part` in a
 * script and narrowing the REFUSAL would break them. Undocumented is "not part
 * of the supported vocabulary", not "forbidden".
 */
export const K_SCRIPT_DOCUMENTED_TOOL_IDS: ReadonlyArray<string> = [
  // Visibility
  AgentToolNames.HideParts,
  AgentToolNames.UnhideParts,
  // Isolation — always paired, or the scene stays faded for the next step
  AgentToolNames.Isolate,
  AgentToolNames.ClearIsolation,
  // Emphasis — likewise paired
  AgentToolNames.HighlightPart,
  AgentToolNames.ClearHighlight,
  // Animation
  AgentToolNames.ToggleModelAnimation,
  // Playback timing (script-only; implemented by WorkflowScriptRunner)
  AgentToolNames.PlayVideo,
  AgentToolNames.WaitFor,
  AgentToolNames.DoNotWait,
];

/**
 * Authorable, but they edit the PROJECT rather than the view — and script
 * `revert` cannot undo them.
 *
 * `WorkflowRevertState` snapshots visibility, transforms, materials, names,
 * spin, isolation and highlight; creating, deleting, renaming or reordering a
 * scene or a model has no snapshot to restore from, in this client or in
 * Unity. So a script that deletes a scene has permanently changed the author's
 * project, whatever its `revert` says.
 *
 * Not excluded — a workflow legitimately might set a scene up — but the model
 * is told, because the failure is silent and unrecoverable.
 */
export const K_SCRIPT_STRUCTURAL_TOOL_IDS: ReadonlySet<string> = new Set(
  [
    AgentToolNames.NewScene,
    AgentToolNames.DuplicateScene,
    AgentToolNames.DeleteScene,
    AgentToolNames.RenameScene,
    AgentToolNames.ReorderScene,
    AgentToolNames.DeleteModel,
    AgentToolNames.RenamePart,
    AgentToolNames.UpdateSceneNotes,
    AgentToolNames.UpdatePartContext,
    AgentToolNames.CreateSelectionSet,
    AgentToolNames.RemoveSelectionSet,
  ].map((id) => id.toLowerCase()),
);

/** True when a command edits the project itself and no `revert` can undo it. */
export function isStructuralScriptCommand(toolName: string): boolean {
  return K_SCRIPT_STRUCTURAL_TOOL_IDS.has(toolName.trim().toLowerCase());
}

/**
 * Read-only lookups the Workflow Builder CALLS while authoring, and must never
 * write into a script. Port of Unity's `s_executeOnlyToolNames`.
 *
 * These are how a reference becomes a real argument: a name the author typed
 * ("the fan") is not a valid `partPathsToIsolate` value, and the only way to
 * turn it into one is to look it up.
 *
 * Reachability is NOT uniform, so do not assume it. Only the four context tools
 * and `take_screenshot` are in `K_CORE_ALWAYS_ALLOWED_TOOL_IDS`; the rest
 * (`Get_Part_Transforms`, `Get_Part_Materials`, `Get_Material_Library`,
 * `Count_Parts`, the doc searches, `Recall_From_Memory`, …) reach a role only
 * when its `tools:` list declares them — which is why `workflow-builder.md`
 * declares the ones it needs. Membership HERE only decides that a command is
 * not authorable as a script step; it grants nothing.
 */
export const K_SCRIPT_CONTEXT_TOOL_IDS: ReadonlySet<string> = new Set(
  [
    AgentToolNames.GetProjectContext,
    AgentToolNames.GetSceneContext,
    AgentToolNames.GetPartContext,
    AgentToolNames.PromptSceneContextAgent,
    AgentToolNames.PromptSearchDocs,
    AgentToolNames.SearchDocByFileName,
    AgentToolNames.GetPartTransforms,
    AgentToolNames.GetPartDimensions,
    AgentToolNames.GetPartMaterials,
    AgentToolNames.GetObjectsFromLibrary,
    AgentToolNames.GetMaterialLibrary,
    AgentToolNames.TakeScreenshot,
    AgentToolNames.CountParts,
    AgentToolNames.RecallFromMemory,
  ].map((id) => id.toLowerCase()),
);

// ---------------------------------------------------------------------------
// Script-only commands
// ---------------------------------------------------------------------------

function arg(
  name: string,
  type: string,
  description: string,
  options: {
    required?: boolean;
    itemType?: string;
    enumValues?: ReadonlyArray<string>;
  } = {},
): ScriptCommandArg {
  return {
    name,
    type,
    itemType: options.itemType ?? "",
    description,
    enumValues: options.enumValues ?? [],
    required: options.required === true,
  };
}

function spec(
  name: string,
  category: string,
  description: string,
  args: ReadonlyArray<ScriptCommandArg>,
): ScriptCommandSpec {
  return {
    name,
    description,
    category,
    properties: new Map(args.map((a) => [a.name, a])),
    required: args.filter((a) => a.required).map((a) => a.name),
    isScriptOnly: true,
  };
}

/**
 * Commands the RUNNER implements, which are not registered agent tools.
 *
 * These exist because playback has a notion of TIME that a live chat turn does
 * not: a lesson step waits for its learner. None is a live tool, and
 * deliberately so — `wait_for`'s live counterpart needs the auto-turn routing
 * the web has not ported, and registering it would let the Assistant park a
 * chat turn on an event that never fires. Inside a script the semantics are
 * self-contained and the runner owns them, which is the same footing
 * `do_not_wait` has always had.
 */
export const SCRIPT_ONLY_COMMANDS: ReadonlyArray<ScriptCommandSpec> = [
  spec(
    AgentToolNames.PlayVideo,
    "Playback",
    "Starts the video on the script's owning step. On a scene step it opens the video full size " +
      "and collapses it again when playback finishes; on a media-only step it plays in the " +
      "already-full-size presentation. Takes no arguments. Use media.autoplay=true instead when " +
      "the video should start immediately on step arrival rather than at a particular point in the script.",
    [],
  ),
  spec(
    AgentToolNames.WaitFor,
    "Playback",
    "Pauses the script here until the learner acts, or for a fixed delay. This is what turns a " +
      "narration into a lesson: the step waits for the learner instead of talking over them.",
    [
      arg(
        "trigger",
        "string",
        "What ends the wait. 'user_response' resumes on the learner's next spoken or typed turn; " +
          "'delay' resumes after delaySeconds.",
        { required: true, enumValues: ["user_response", "delay"] },
      ),
      arg(
        "delaySeconds",
        "number",
        "Seconds to wait when trigger=delay. Required for that trigger; ignored otherwise. " +
          "Capped at 300.",
      ),
      arg(
        "userResponseCondition",
        "string",
        "What the learner has to say or do for the wait to count as satisfied (e.g. 'confirms the " +
          "cover is off'). Recorded for the guide to read; it does not gate resumption.",
      ),
    ],
  ),
  spec(
    AgentToolNames.DoNotWait,
    "Playback",
    "Overlap marker. Placed as its own step immediately AFTER another step, it lets that previous " +
      "step keep running while the script moves on — for example to start a highlight while Spark " +
      "is still speaking. It affects the step BEFORE it, never the step after it, and takes no " +
      "arguments.",
    [],
  ),
];

const K_SCRIPT_ONLY_BY_ID: ReadonlyMap<string, ScriptCommandSpec> = new Map(
  SCRIPT_ONLY_COMMANDS.map((command) => [command.name.toLowerCase(), command]),
);

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

let s_registered: Map<string, ScriptCommandSpec> | null = null;

function readArg(name: string, raw: unknown, required: ReadonlySet<string>): ScriptCommandArg {
  const schema =
    raw !== null && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};

  const items =
    schema.items !== null && typeof schema.items === "object" && !Array.isArray(schema.items)
      ? (schema.items as Record<string, unknown>)
      : null;

  const enumValues = Array.isArray(schema.enum)
    ? schema.enum.filter((value): value is string => typeof value === "string")
    : [];

  return {
    name,
    type: typeof schema.type === "string" ? schema.type : "",
    itemType: items !== null && typeof items.type === "string" ? items.type : "",
    description: typeof schema.description === "string" ? schema.description : "",
    enumValues,
    required: required.has(name),
  };
}

/**
 * Snapshots the live registry. Called at registry construction with the same
 * tool records the executor dispatches on, which is what makes the published
 * command reference incapable of drifting from the real tools.
 */
export function setScriptCommandCatalog(
  tools: ReadonlyArray<{
    name: string;
    description?: string;
    category?: string;
    parameters: Record<string, unknown>;
  }>,
): void {
  const next = new Map<string, ScriptCommandSpec>();

  for (const tool of tools) {
    const rawProperties = tool.parameters["properties"];
    const properties =
      rawProperties !== null && typeof rawProperties === "object" && !Array.isArray(rawProperties)
        ? (rawProperties as Record<string, unknown>)
        : {};

    const required = new Set(
      Array.isArray(tool.parameters["required"])
        ? (tool.parameters["required"] as unknown[]).filter(
            (value): value is string => typeof value === "string",
          )
        : [],
    );

    const args = new Map<string, ScriptCommandArg>();
    for (const [key, raw] of Object.entries(properties)) {
      args.set(key, readArg(key, raw, required));
    }

    next.set(tool.name.trim().toLowerCase(), {
      name: tool.name.trim(),
      description: tool.description ?? "",
      category: tool.category ?? "",
      properties: args,
      required: [...required],
      isScriptOnly: false,
    });
  }

  s_registered = next;
}

/**
 * Whether {@link setScriptCommandCatalog} has run.
 *
 * The distinction matters to validation: before the registry is built, every
 * lookup answers `null`, and reporting "this tool does not exist in this
 * client" from that state would flag a whole valid document. A caller that
 * treats absence as a defect must gate on this; a caller that treats absence
 * as "cannot judge" does not need it.
 */
export function isScriptCommandCatalogReady(): boolean {
  return s_registered !== null;
}

/**
 * The spec for one script command, or `null` when the id is neither a
 * registered tool nor a script-only command.
 *
 * Script-only commands answer even before the registry is filled, because they
 * are static — which is what lets a headless parse validate a `wait_for` step.
 */
export function getScriptCommandSpec(toolName: string): ScriptCommandSpec | null {
  const key = toolName.trim().toLowerCase();
  const scriptOnly = K_SCRIPT_ONLY_BY_ID.get(key);
  if (scriptOnly) return scriptOnly;
  if (s_registered === null) return null;
  return s_registered.get(key) ?? null;
}

/**
 * Parameter names for a registered tool or script-only command, or `null` when
 * the id is unknown / the catalog has not been filled.
 *
 * Retains the contract the older `toolArgKeys` shim had: `null` means "cannot
 * judge", which callers must treat as "skip the check", never as "no arguments
 * are allowed".
 */
export function getToolArgKeys(toolName: string): ReadonlySet<string> | null {
  const command = getScriptCommandSpec(toolName);
  if (command === null) return null;
  return new Set(command.properties.keys());
}

/** True when a registered tool is a read-only lookup rather than a lesson step. */
export function isScriptContextTool(toolName: string): boolean {
  return K_SCRIPT_CONTEXT_TOOL_IDS.has(toolName.trim().toLowerCase());
}

/** True when the id may appear as a script step's `tool`. */
export function isAuthorableScriptCommand(toolName: string): boolean {
  const key = toolName.trim().toLowerCase();
  if (K_SCRIPT_ONLY_BY_ID.has(key)) return true;
  if (s_registered === null || !s_registered.has(key)) return false;
  return !K_SCRIPT_EXCLUDED_TOOL_IDS.has(key) && !K_SCRIPT_CONTEXT_TOOL_IDS.has(key);
}

/**
 * The commands the published reference documents, in
 * {@link K_SCRIPT_DOCUMENTED_TOOL_IDS} order — which is the order a lesson
 * reads, not alphabetical or by category.
 *
 * A documented id the registry does not carry is skipped rather than rendered
 * as an empty entry: the web registry is a subset, and a reference that
 * described a tool this build cannot run would be worse than a shorter one.
 */
export function listScriptCommands(): ReadonlyArray<ScriptCommandSpec> {
  const specs: ScriptCommandSpec[] = [];
  for (const id of K_SCRIPT_DOCUMENTED_TOOL_IDS) {
    const spec = getScriptCommandSpec(id);
    if (spec !== null) specs.push(spec);
  }
  return specs;
}

/** The read-only lookups the authoring agent may call, for documentation. */
export function listScriptContextCommands(): ReadonlyArray<ScriptCommandSpec> {
  return [...(s_registered?.values() ?? [])]
    .filter((command) => K_SCRIPT_CONTEXT_TOOL_IDS.has(command.name.toLowerCase()))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Argument classification — what KIND of control an argument needs
// ---------------------------------------------------------------------------

/**
 * Argument keys that name a scene reference. The web registry follows a strict
 * naming convention (`partPathsToHide`, `selectionSetNameToSelect`,
 * `textBoxNamesToUnhide`, `modelPathsToExplode`), so these patterns cover a
 * tool added later without an edit here.
 *
 * They live in THIS module rather than beside either consumer because both of
 * them — the playback revert snapshot and the authoring UI's argument controls
 * — are asking the same question of the same data, and a second copy of the
 * convention is how the two come to disagree about which arguments name parts.
 */
const K_PART_PATH_ARG = /^(?:part|model)Paths?(?:[A-Z]|$)/;
const K_SELECTION_SET_ARG = /^selectionSetNames?(?:[A-Z]|$)/;
const K_TEXT_BOX_ARG = /^textBoxNames?(?:[A-Z]|$)/;

/** True when the argument names parts by hierarchy path (`0,1,2`). */
export function isPartPathArgName(name: string): boolean {
  return K_PART_PATH_ARG.test(name);
}

/** True when the argument names selection sets. */
export function isSelectionSetArgName(name: string): boolean {
  return K_SELECTION_SET_ARG.test(name);
}

/** True when the argument names text boxes / cards. */
export function isTextBoxArgName(name: string): boolean {
  return K_TEXT_BOX_ARG.test(name);
}

/**
 * The control an argument needs in the authoring UI.
 *
 * Derived from the argument's declared TYPE and its NAME, in that order of
 * reliability: `enum`, `boolean` and `number` come straight from the schema,
 * while "this string is a part path" is only knowable from the naming
 * convention above — the registry's own descriptions say it in prose
 * ("Hierarchy paths from Get_Scene_Context"), which is not something to match
 * on.
 *
 * `strings` is the honest fallback for a list whose members this client cannot
 * enumerate: a free-text chip list, not a menu, because offering a menu that
 * cannot contain the right answer is worse than offering none.
 */
export type ScriptCommandArgKind =
  | "enum"
  | "boolean"
  | "number"
  | "part_paths"
  | "selection_sets"
  | "text_boxes"
  | "strings"
  | "text";

export function classifyScriptCommandArg(arg: ScriptCommandArg): ScriptCommandArgKind {
  if (arg.enumValues.length > 0) return "enum";
  if (arg.type === "boolean") return "boolean";
  if (arg.type === "number" || arg.type === "integer") return "number";
  if (isPartPathArgName(arg.name)) return "part_paths";
  if (isSelectionSetArgName(arg.name)) return "selection_sets";
  if (isTextBoxArgName(arg.name)) return "text_boxes";
  if (arg.type === "array") return "strings";
  return "text";
}

/** True when the argument holds a LIST of values rather than a single one. */
export function isScriptCommandArgList(arg: ScriptCommandArg): boolean {
  return arg.type === "array";
}
